
import { EnhancedPEOProviderProfile } from '@/components/peo/profile/EnhancedPEOProviderProfile';
import { QuotesList } from './QuotesList';
import { UserManagementDashboard } from '@/components/peo/enterprise/UserManagementDashboard';
import { SubscriptionManagement } from '@/components/peo/enterprise/SubscriptionManagement';
import { AnalyticsDashboard } from '@/components/peo/analytics/AnalyticsDashboard';
import { usePEOEnterpriseFeatures } from '@/hooks/peo/usePEOEnterpriseFeatures';
import { SidebarProvider, Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Users, BarChart, FileText, Home, Settings, LogOut, Bell, Search, Filter, Download, TrendingUp, DollarSign, Clock, CheckCircle, Plus, MessageSquare } from 'lucide-react';
import { CreateApplicationDialog } from '@/components/peo/enterprise/CreateApplicationDialog';
import { useState } from 'react';
import { toast } from 'sonner';
import { AssignmentInbox } from './AssignmentInbox';
import { QuotePipeline } from './QuotePipeline';
import { ComingSoonFeature } from './ComingSoonFeature';
import { useOrganizationStats } from '@/hooks/peo-organizations/useOrganizationStats';
import { useProviderQuotes } from './hooks/useProviderQuotes';

interface PEODashboardContentProps {
  loading: boolean;
  onRefresh: () => void;
  onLogout: () => void;
  providerInfo: any;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  form: {
    name: string;
    contact_email: string;
    years_in_business: string;
    employee_range: string;
  };
  setForm: React.Dispatch<React.SetStateAction<any>>;
  onSave: (e: React.FormEvent) => void;
  saving: boolean;
}

const PEOSidebar = ({ 
  providerInfo, 
  onLogout, 
  activeSection, 
  setActiveSection 
}: { 
  providerInfo: any; 
  onLogout: () => void; 
  activeSection: string; 
  setActiveSection: (section: string) => void; 
}) => {
  const { hasEnterpriseAccess } = usePEOEnterpriseFeatures(providerInfo);
  
  const menuItems = [
    { title: "Overview", icon: Home, section: "overview" },
    { title: "Assignments", icon: Users, section: "assignments" },
    { title: "Quote Pipeline", icon: FileText, section: "pipeline", enterprise: true },
    { title: "Team Collaboration", icon: MessageSquare, section: "collaboration", enterprise: true },
    { title: "Analytics", icon: BarChart, section: "analytics", enterprise: true },
    { title: "Profile", icon: Settings, section: "profile" },
    { title: "Subscription", icon: Crown, section: "subscription" },
  ];

  return (
    <Sidebar className="border-r border-border/40">
      <SidebarContent className="bg-card">
        <div className="p-6 border-b border-border/40">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Crown className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">{providerInfo?.name || 'PEO Provider'}</h2>
              <p className="text-sm text-muted-foreground">Enterprise Dashboard</p>
            </div>
          </div>
        </div>
        
        <SidebarGroup>
          <SidebarGroupLabel>Main Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton 
            onClick={() => setActiveSection(item.section)}
            disabled={item.enterprise && !hasEnterpriseAccess}
            className={`w-full justify-start ${
              activeSection === item.section 
                ? "bg-primary text-primary-foreground" 
                : "hover:bg-accent hover:text-accent-foreground"
            } ${item.enterprise && !hasEnterpriseAccess ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <item.icon className="h-4 w-4 mr-3" />
            <span>{item.title}</span>
            {item.enterprise && (
              <Crown className="h-3 w-3 ml-auto text-amber-500" />
            )}
          </SidebarMenuButton>
        </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className="mt-auto p-4 border-t border-border/40">
          <Button 
            variant="ghost" 
            onClick={onLogout}
            className="w-full justify-start text-muted-foreground hover:text-foreground"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </SidebarContent>
    </Sidebar>
  );
};

const DashboardOverview = ({ 
  providerInfo, 
  setActiveSection 
}: { 
  providerInfo: any; 
  setActiveSection: (section: string) => void; 
}) => {
  const { stats: orgStats, isLoading: statsLoading } = useOrganizationStats(providerInfo?.id);
  const { data: quotes = [], isLoading: quotesLoading } = useProviderQuotes(providerInfo?.id);

  console.log('DashboardOverview: Raw quotes data:', quotes);
  console.log('DashboardOverview: Org stats:', orgStats);
  
  // Calculate real metrics with safe fallbacks
  const activeQuotes = Array.isArray(quotes) ? quotes.filter(q => q.status === 'pending' || q.status === 'interested').length : 0;
  
  // Calculate monthly revenue from accepted quotes (using monthly_cost)
  const monthlyRevenue = Array.isArray(quotes) 
    ? quotes.filter(q => q.status === 'accepted')
        .reduce((total, quote) => total + (Number(quote.monthly_cost) || 0), 0)
    : 0;
  
  // Response time (average from orgStats, convert to hours with decimal)
  const responseTimeHours = orgStats?.averageResponseTime || 0;
  const responseTimeDisplay = responseTimeHours === 0 
    ? '0h'
    : responseTimeHours < 1 
      ? `${Math.round(responseTimeHours * 60)}m`
      : responseTimeHours < 24 
        ? `${responseTimeHours.toFixed(1)}h`
        : `${Math.round(responseTimeHours / 24)}d`;
  
  // Success rate (accepted quotes / total submitted quotes)
  const submittedQuotes = Array.isArray(quotes) ? quotes.filter(q => q.status !== 'pending').length : 0;
  const acceptedQuotes = Array.isArray(quotes) ? quotes.filter(q => q.status === 'accepted').length : 0;
  const successRate = submittedQuotes > 0 ? Math.round((acceptedQuotes / submittedQuotes) * 100) : 0;
  
  console.log('DashboardOverview: Calculated metrics:', {
    activeQuotes,
    monthlyRevenue,
    responseTimeHours,
    successRate,
    submittedQuotes,
    acceptedQuotes
  });

  const stats = [
    { 
      title: "Active Quotes", 
      value: activeQuotes.toString(), 
      icon: FileText, 
      trend: activeQuotes > 0 ? `${activeQuotes} active` : "No active quotes", 
      color: "text-blue-600" 
    },
    { 
      title: "Monthly Revenue", 
      value: `$${monthlyRevenue.toLocaleString()}`, 
      icon: DollarSign, 
      trend: `${acceptedQuotes} accepted`, 
      color: "text-green-600" 
    },
    { 
      title: "Response Time", 
      value: responseTimeDisplay, 
      icon: Clock, 
      trend: `Avg. response time`, 
      color: "text-purple-600" 
    },
    { 
      title: "Success Rate", 
      value: `${successRate}%`, 
      icon: CheckCircle, 
      trend: `${acceptedQuotes}/${submittedQuotes} accepted`, 
      color: "text-emerald-600" 
    },
  ];

  const isLoading = statsLoading || quotesLoading;

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => (
          <Card key={stat.title} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                  <p className="text-2xl font-bold">
                    {isLoading ? '...' : stat.value}
                  </p>
                  <div className="flex items-center space-x-1 mt-1">
                    <span className="text-xs text-muted-foreground">{stat.trend}</span>
                  </div>
                </div>
                <div className={`w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Streamline your daily workflow</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Button 
              className="h-20 flex-col space-y-2" 
              variant="outline"
              onClick={() => setActiveSection('quotes')}
            >
              <FileText className="h-6 w-6" />
              <span>Create Quote</span>
            </Button>
            <Button 
              className="h-20 flex-col space-y-2" 
              variant="outline"
              onClick={() => setActiveSection('applications')}
            >
              <Users className="h-6 w-6" />
              <span>Manage Applications</span>
            </Button>
            <Button 
              className="h-20 flex-col space-y-2" 
              variant="outline"
              onClick={() => setActiveSection('analytics')}
            >
              <BarChart className="h-6 w-6" />
              <span>View Analytics</span>
            </Button>
            {providerInfo && (
              <CreateApplicationDialog 
                peoProviderId={providerInfo.id}
                onApplicationCreated={() => toast.success('Application created successfully!')}
                buttonClassName="h-20 flex-col space-y-2"
                buttonVariant="outline"
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const PEODashboardContent = ({
  loading,
  onRefresh,
  onLogout,
  providerInfo,
  isEditing,
  setIsEditing,
  form,
  setForm,
  onSave,
  saving,
}: PEODashboardContentProps) => {
  const { hasEnterpriseAccess } = usePEOEnterpriseFeatures(providerInfo);
  const [activeSection, setActiveSection] = useState('overview');

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return <DashboardOverview providerInfo={providerInfo} setActiveSection={setActiveSection} />;
      case 'quotes':
        return providerInfo && <QuotesList providerId={providerInfo.id} />;
      case 'assignments':
        return <AssignmentInbox providerId={providerInfo?.id} />;
      case 'pipeline':
        if (!hasEnterpriseAccess) {
          return (
            <ComingSoonFeature
              title="Quote Pipeline"
              description="Advanced pipeline management for tracking quotes and conversions"
              isEnterprise={true}
              estimatedRelease="Q1 2024"
            />
          );
        }
        return <QuotePipeline providerId={providerInfo?.id} />;
      case 'collaboration':
        if (!hasEnterpriseAccess) {
          return (
            <ComingSoonFeature
              title="Team Collaboration"
              description="Advanced team coordination and communication tools"
              isEnterprise={true}
              estimatedRelease="Q4 2025"
            />
          );
        }
        return (
          <ComingSoonFeature
            title="Team Collaboration"
            description="Advanced team coordination and communication tools"
            isEnterprise={true}
            estimatedRelease="Q4 2025"
          />
        );
      case 'analytics':
        if (!hasEnterpriseAccess) {
          return (
            <ComingSoonFeature
              title="Analytics Dashboard"
              description="Advanced analytics and reporting for your PEO operations"
              isEnterprise={true}
              estimatedRelease="Q1 2024"
            />
          );
        }
        return <AnalyticsDashboard />;
      case 'profile':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Provider Profile</CardTitle>
              <CardDescription>Manage your company information</CardDescription>
            </CardHeader>
            <CardContent>
              <EnhancedPEOProviderProfile
                providerInfo={providerInfo}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
                form={form}
                setForm={setForm}
                onSave={onSave}
                saving={saving}
              />
            </CardContent>
          </Card>
        );
      case 'subscription':
        return <SubscriptionManagement providerInfo={providerInfo} />;
      default:
        return <DashboardOverview providerInfo={providerInfo} setActiveSection={setActiveSection} />;
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <PEOSidebar 
          providerInfo={providerInfo} 
          onLogout={onLogout}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
        
        <main className="flex-1 overflow-hidden">
          {/* Header */}
          <header className="h-16 border-b border-border/40 bg-card/50 backdrop-blur supports-[backdrop-filter]:bg-card/50">
            <div className="h-full px-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <SidebarTrigger />
                <div>
                  <h1 className="text-lg font-semibold">PEO Dashboard</h1>
                  <p className="text-sm text-muted-foreground">Welcome back, {providerInfo?.name}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <Button variant="outline" size="sm" onClick={onRefresh}>
                  <Bell className="h-4 w-4" />
                </Button>
                {hasEnterpriseAccess && (
                  <Badge variant="secondary" className="text-xs">
                    <Crown className="h-3 w-3 mr-1" />
                    Enterprise
                  </Badge>
                )}
              </div>
            </div>
          </header>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {renderContent()}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};
